import { ArrowLeft, CheckCircle2, Loader2, Lock, Mail, MessageCircle, Phone } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import PhoneNumberInput from './PhoneNumberInput';
import useOtpAuth, { OTP_STATES } from './useOtpAuth';

const methodMeta = {
	email: { label: 'Email', Icon: Mail },
	phone: { label: 'Phone', Icon: Phone },
};

export default function OtpAuthFlow({ allowedContacts, initialMethod, requestId, linkToken, onAuthenticated, onCancel }) {
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState(null);
	const [phoneCountry, setPhoneCountry] = useState(null);
	const [validationError, setValidationError] = useState('');
	const stablePhoneChange = useCallback((value) => setPhone(value), []);
	const stablePhoneCountryChange = useCallback((value) => setPhoneCountry(value), []);
	const methods = useMemo(() => Object.keys(allowedContacts), [allowedContacts]);
	const handleAuthenticated = useCallback((response, context = {}) => {
		return onAuthenticated(response, context.method === 'phone'
			? { ...context, country: phoneCountry }
			: context);
	}, [onAuthenticated, phoneCountry]);
	const flow = useOtpAuth({ allowedContacts, initialMethod, requestId, linkToken, onAuthenticated: handleAuthenticated });
	const contact = allowedContacts[flow.method];
	const busy = [OTP_STATES.SENDING_CODE, OTP_STATES.VERIFYING_CODE, OTP_STATES.REGISTERING, OTP_STATES.AUTHENTICATED].includes(flow.state);

	const handleSend = () => {
		const identifier = flow.method === 'email' ? email.trim().toLowerCase() : phone;
		if (!contact.requestBound) {
			if (flow.method === 'email' && !/^\S+@\S+\.\S+$/.test(identifier)) {
				setValidationError('Enter a valid email address.');
				return;
			}
			if (flow.method === 'phone' && !identifier) {
				setValidationError('Select a country and enter a valid phone number.');
				return;
			}
		}
		setValidationError('');
		flow.sendCode(identifier);
	};

	if ([OTP_STATES.ENTER_CONTACT, OTP_STATES.SENDING_CODE].includes(flow.state)) {
		return (
			<div className="space-y-5">
				{methods.length > 1 && (
					<div className="grid h-12 grid-cols-2 rounded border border-slate-300 bg-slate-100 p-1" aria-label="Sign-in method">
						{methods.map((method) => {
							const { Icon, label } = methodMeta[method];
							return (
								<button key={method} type="button" aria-pressed={flow.method === method} disabled={busy} onClick={() => { flow.changeMethod(method); setValidationError(''); }}
									className={`flex min-w-0 items-center justify-center gap-2 rounded px-3 text-sm font-semibold transition-colors ${flow.method === method ? 'bg-cyan-800 text-white shadow-sm' : 'text-slate-700 hover:bg-white'}`}>
									<Icon className="h-4 w-4" /> {label}
								</button>
							);
						})}
					</div>
				)}

				<div>
					<label htmlFor={`otp-contact-${flow.method}`} className="mb-2 block text-sm font-semibold text-slate-700">{methodMeta[flow.method].label}</label>
					{contact.locked ? (
						<div className="relative flex h-12 items-center rounded border border-slate-300 bg-slate-100 px-4 text-slate-700">
							<span className="truncate">{contact.maskedValue}</span><Lock className="ml-auto h-4 w-4 text-slate-400" />
						</div>
					) : flow.method === 'email' ? (
						<input id="otp-contact-email" type="email" autoComplete="email" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)}
							placeholder="name@example.com" className="input-field h-12" />
					) : (
						<PhoneNumberInput disabled={busy} onChange={stablePhoneChange} onCountryChange={stablePhoneCountryChange} invalid={Boolean(validationError)} />
					)}
					{flow.method === 'phone' && !contact.locked && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MessageCircle className="h-3.5 w-3.5" />The code will arrive through WhatsApp.</p>}
				</div>

				{(validationError || flow.error) && <div role="status" aria-live="polite" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{validationError || flow.error}</div>}
				<button type="button" onClick={handleSend} disabled={busy || flow.countdown > 0} className="btn-primary h-12 w-full">
					{flow.state === OTP_STATES.SENDING_CODE ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : flow.countdown > 0 && flow.error ? `Try again in ${flow.countdown}s` : 'Send verification code'}
				</button>
				{onCancel && <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary h-12 w-full"><ArrowLeft className="mr-2 inline h-4 w-4" />Back</button>}
			</div>
		);
	}

	if ([OTP_STATES.CONFIRM_REGISTRATION, OTP_STATES.REGISTERING].includes(flow.state)) {
		const identityLabel = flow.method === 'phone' ? 'phone number' : 'email';
		return (
			<div className="space-y-5">
				<div className="rounded border border-emerald-200 bg-emerald-50 p-4">
					<div className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" /><div><p className="font-semibold leading-6 text-emerald-950">This {identityLabel} isn't registered yet, but your code has been verified.</p><p className="mt-1 text-sm leading-5 text-emerald-800">You can create your account now and sign in immediately without requesting another code.</p></div></div>
				</div>
				{flow.error && <div role="status" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{flow.error}</div>}
				<button type="button" onClick={flow.register} disabled={busy} className="btn-primary h-12 w-full">{flow.state === OTP_STATES.REGISTERING ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Create account & sign in'}</button>
				<button type="button" onClick={flow.back} disabled={busy} className="btn-secondary h-12 w-full">Use another {identityLabel}</button>
			</div>
		);
	}

	return (
		<div className="space-y-5">
			<div className="text-center"><p className="text-sm text-slate-600">Sent {flow.method === 'phone' ? 'via WhatsApp' : 'by email'} to</p><p className="mt-1 font-semibold text-slate-900">{flow.maskedDestination}</p></div>
			<div><label htmlFor="otp-verification-code" className="mb-2 block text-sm font-semibold text-slate-700">Verification code</label><input id="otp-verification-code" autoFocus type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={flow.code} onChange={(event) => flow.setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} className="input-field h-12 text-center text-xl" /></div>
			{flow.error && <div role="status" aria-live="polite" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{flow.error}</div>}
			<button type="button" onClick={flow.verify} disabled={busy || flow.code.length !== 6} className="btn-primary h-12 w-full">{flow.state === OTP_STATES.VERIFYING_CODE ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Verify and continue'}</button>
			<button type="button" onClick={flow.resend} disabled={busy || flow.countdown > 0} className="h-10 w-full text-sm font-semibold text-cyan-800 disabled:text-slate-400">{flow.countdown > 0 ? `Resend in ${flow.countdown}s` : 'Resend code'}</button>
			<button type="button" onClick={flow.back} disabled={busy} className="btn-secondary h-12 w-full"><ArrowLeft className="mr-2 inline h-4 w-4" />Back</button>
		</div>
	);
}
